// FIXED: Use runtime configuration for API URL
import { auth } from "./firebase";
import { API_BASE_URL } from "./config/runtime";
import { getProofOfWork } from "./utils/proofOfWork";

// DEV-ONLY user impersonation (set by run_local.sh --login-as). Guarded behind
// import.meta.env.DEV so it is stripped from production builds entirely. When
// set, every request carries an X-Dev-User-ID header, which the backend honors
// only when DISABLE_AUTH=true in a non-production environment (see auth.py).
export const DEV_IMPERSONATE_UID = import.meta.env.DEV
  ? import.meta.env.VITE_DEV_IMPERSONATE_UID
  : undefined;
const devImpersonationHeaders = DEV_IMPERSONATE_UID
  ? { "X-Dev-User-ID": DEV_IMPERSONATE_UID }
  : {};

// How long to wait before asking Firebase whether a session is really gone. Long enough for a
// token refresh, a wifi reconnect or a laptop waking up to settle on its own.
const SESSION_RECHECK_DELAY_MS = 10000;

/* ════════════════════════════════════════════════════════════════════════════════════════
   TRANSPORT RESILIENCE — why this lives here and nowhere else
   ════════════════════════════════════════════════════════════════════════════════════════

   `Failed to fetch` is the browser's message for a request that got NO HTTP RESPONSE AT ALL:
   connection refused or reset, DNS, a rejected CORS preflight. It is not a 4xx or 5xx — those
   come back as real responses and are handled below. So it is a TRANSPORT fault, and the only
   thing that reliably produces one here is the server being momentarily absent: a uvicorn
   reload in dev, a Cloud Run cold start or scale-in in production, a laptop waking up.

   AUDITED 2026-10-13 across all 46 admin files: 37 of the 43 network call sites had no retry
   of any kind. The six that did were protected only because they happened to use the
   `useAdminFetch` hook — every bare `useEffect` fetch, every one of the six polling timers,
   and every mutation went straight to `fetch` and surfaced the raw TypeError. That is why the
   admin panel showed "Failed to fetch" constantly rather than occasionally: a single eight-
   second restart catches several polls and ~20 mount-time calls at once.

   Patching 37 call sites would fix today and not tomorrow — the next panel somebody adds
   starts unprotected again. This function is the one layer all 43 share, so the retry goes
   here, once.

   GET ONLY. Retrying a POST could apply a mutation twice: the request may well have REACHED
   the server and died on the way back. The in-flight coalescing below already reasons this
   way and for the same reason.                                                              */
const TRANSPORT_RETRIES = 2;                       // 3 attempts in total
const TRANSPORT_BACKOFF_MS = [250, 750];           // + jitter, see _backoff
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// Jittered, so a page that fired twenty requests into a restarting backend does not retry all
// twenty in the same millisecond and hand the just-booted server the same thundering herd.
const _backoff = (attempt) =>
  TRANSPORT_BACKOFF_MS[attempt] * (0.75 + Math.random() * 0.5);

/** True for a genuine transport failure, false for anything the server actually answered. */
const _isTransportError = (err) =>
  err instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(err?.message || "");

/* ════════════════════════════════════════════════════════════════════════════════════════
   THE READ CACHE — and why it is OPT-IN rather than blanket
   ════════════════════════════════════════════════════════════════════════════════════════

   The backend already caches the heavy admin endpoints properly: `api/_admin_cache.py` is a
   two-tier memory-then-Firestore cache with stale-while-revalidate, single-flight and a
   fleet-wide rebuild lease, written after a measured 38.2M Firestore reads / $22.39 in a
   month. What it cannot do is remove the ROUND TRIP. The map below is in-flight COALESCING
   only — its own comment says "not a cache: the entry is dropped the moment the request
   settles" — so five components wanting the same payload still make five requests unless they
   happen to overlap, and every tab switch refetches from scratch.

   Measured example: `/api/admin/anon-users` has FIVE independent callers (AnonymousDashboard,
   AnonymousBreakdowns, AnonymousConversionFunnel, DevicesPanel, and UserAnalyticsDashboard
   rendering a fifth). Each one is a full `anon_users` collection scan plus a Firebase Auth
   lookup per converted uid, and that endpoint is not server-cached at all.

   OPT-IN, NOT BLANKET, and this is the important part. A default TTL over every GET would
   quietly stale the live views — the reel queue, the parked-selection poll, a job's status —
   where being a few seconds behind is the whole failure. So nothing is cached unless it is
   listed here, and what is listed is the slow-moving aggregate data the server itself already
   considers cacheable. Each TTL is deliberately SHORTER than the server's, so the client can
   never serve something staler than the server would have.                                   */
const READ_CACHE_TTL_MS = [
  // Not server-cached at all, and five callers — the single biggest win in the table.
  [/^\/api\/admin\/anon-users(\?|$)/, 120_000],
  // Server TTL 120s / 1h stale. Five callers: AnalyticsDashboard, PerformanceSection,
  // CapacitySummary, CapacityPanel, DailyActiveCapacity.
  [/^\/api\/admin\/analytics(\?|$)/, 60_000],
  // Server TTL 600s / 24h stale. Three callers each.
  [/^\/api\/admin\/capacity-inputs(\?|$)/, 300_000],
  [/^\/api\/admin\/rate-headroom(\?|$)/, 300_000],
  // Server TTL 120s / 600s stale; the batched six-payload endpoint.
  [/^\/api\/admin\/dashboard(\?|$)/, 60_000],
  [/^\/api\/admin\/cohorts(\?|$)/, 300_000],          // server 900s
  [/^\/api\/admin\/user-analytics(\?|$)/, 120_000],
  [/^\/api\/admin\/funnel-analytics(\?|$)/, 60_000],
  [/^\/api\/admin\/project-funnel(\?|$)/, 120_000],   // server 300s
  [/^\/api\/admin\/projects(\?|$)/, 60_000],
  [/^\/api\/admin\/free-video-cap(\?|$)/, 60_000],
  [/^\/api\/admin\/acquisition(\?|$)/, 60_000],   // was /signups-by-source: a path that never existed
  [/^\/api\/admin\/stripe-revenue(\?|$)/, 120_000],
  [/^\/api\/admin\/reel-cost-basis(\?|$)/, 300_000],  // server 600s
  [/^\/api\/admin\/campaigns\/(audience|send-allowance|weekly-digest)(\?|$)/, 60_000],
  [/^\/api\/admin\/youtube\/(status|engagement)(\?|$)/, 60_000],
  [/^\/api\/admin\/service-alerts(\?|$)/, 60_000],
  // DELIBERATELY ABSENT, and each absence is a decision:
  //   /reels, /reel-selections, /job-timeline, /reel-editor/*  — live views; a stale queue is
  //       the bug, not the fix.
  //   /site-controls  — read immediately after a write by the card that wrote it.
  //   /prompt-packages  — the Workshop edits it and expects its own change back.
];

const _cacheTtlFor = (endpoint) => {
  for (const [pattern, ttl] of READ_CACHE_TTL_MS) if (pattern.test(endpoint)) return ttl;
  return 0;                                        // not listed = not cached
};

/* WHAT A WRITE INVALIDATES. A mutation under /api/admin can change almost anything an admin
   GET reports — applying a limit moves the capacity model, a backfill moves the anon figures —
   and working out the exact dependency per endpoint would be a table nobody maintains. So any
   admin write clears every admin read. It is blunt and it is CORRECT, which is the right trade
   when the alternative is a stale number on a page whose whole job is reporting numbers. The
   refill is cheap: the server's own cache absorbs it. */
const _invalidatesOn = (endpoint) => (endpoint.startsWith("/api/admin/") ? "/api/admin/" : null);

class ApiService {
  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.authReady = false;
    this.authReadyAt = 0;
    this._verifyingSession = false;
    // In-flight GETs, keyed on the full path+query. Request COALESCING, not a cache: the
    // entry is dropped the moment the request settles, so a later fetch of the same URL
    // still goes to the network. See makeRequest.
    this._inflightGets = new Map();
    /* THE READ CACHE: endpoint -> { data, ts }. Distinct from `_inflightGets` above, which
       only de-duplicates requests that overlap IN FLIGHT and is dropped the moment one
       settles. This holds the ANSWER for a listed TTL afterwards, which is the half that was
       missing — see READ_CACHE_TTL_MS for why it is opt-in and how the TTLs were chosen. */
    this._readCache = new Map();
    console.log("🔗 API Service initialized with URL:", this.baseUrl);
  }

  setAuthReady(ready) {
    this.authReady = ready;
    if (ready) {
      this.authReadyAt = Date.now();
    }
  }

  // Helper method for making requests with automatic token refresh retry
  /**
   * De-duplicate simultaneous identical GETs, then delegate.
   *
   * WHY. Three separate admin components request /api/admin/analytics on every page open —
   * CapacityPanel, DailyTables and DailyActiveCapacity — and with no coalescing that was three
   * HTTP requests for one answer. Measured on prod: run concurrently they took 8.1s, 8.5s and
   * 10.5s, because they contend rather than share. The backend now caches that endpoint, which
   * fixes the cost; this fixes the count, and either alone would have been enough. Both,
   * because this class of regression has come back three times.
   *
   * THREE THINGS THIS MUST NOT DO, each of which is a way to turn a speed fix into a data bug:
   *
   *   GET ONLY. Coalescing two POSTs would collapse two distinct mutations into one.
   *
   *   NEVER COALESCE THE 401 RETRY. makeRequest calls itself with isRetry=true after
   *   refreshing a token, and that inner call happens while the OUTER one is still in flight —
   *   it would find its own promise in the map and await itself forever.
   *
   *   DROP THE ENTRY WHEN IT SETTLES, not when it succeeds. A rejected promise left in the map
   *   serves the same failure to every later caller, and useAdminFetch retries three times —
   *   all three would join the same dead promise and report failure instantly.
   */
  async makeRequest(endpoint, options = {}, isRetry = false) {
    const method = (options.method || "GET").toUpperCase();
    if (method !== "GET" || isRetry) {
      // A WRITE DROPS THE READS. Done BEFORE the request rather than after: if the call
      // throws we do not know whether the server applied it, and a cache cleared needlessly
      // costs one refetch while a cache left stale after a successful write shows a number
      // that is no longer true.
      const prefix = _invalidatesOn(endpoint);
      if (prefix && !isRetry) this.invalidateCache(prefix);
      return this._performRequest(endpoint, options, isRetry);
    }

    /* `force: true` is how a Refresh button means refresh. It skips the cached copy but
       still joins an in-flight request — two components refreshing at once is still one
       request, which is what coalescing is for. */
    const ttl = options.force ? 0 : _cacheTtlFor(endpoint);
    if (ttl > 0) {
      const hit = this._readCache.get(endpoint);
      if (hit && Date.now() - hit.ts < ttl) return hit.data;
    }

    const existing = this._inflightGets.get(endpoint);
    if (existing) return existing;

    const inflight = this._performRequest(endpoint, options, false)
      .then((data) => {
        // Only successful reads are remembered. A rejection must never be cached — see the
        // note on dropping the in-flight entry when it SETTLES rather than when it succeeds.
        if (_cacheTtlFor(endpoint) > 0) {
          this._readCache.set(endpoint, { data, ts: Date.now() });
        }
        return data;
      })
      .finally(() => this._inflightGets.delete(endpoint));
    this._inflightGets.set(endpoint, inflight);
    return inflight;
  }

  /** Drop cached reads. No argument clears everything; a prefix clears one area.
   *  Exposed so a caller that knows it has invalidated something the table above cannot see
   *  — a Modal-side change, a job finishing — can say so. */
  invalidateCache(prefix = "") {
    if (!prefix) { this._readCache.clear(); return; }
    for (const key of this._readCache.keys()) {
      if (key.startsWith(prefix)) this._readCache.delete(key);
    }
  }

  async _performRequest(endpoint, options = {}, isRetry = false) {
    const url = `${this.baseUrl}${endpoint}`;

    // Prepare headers with authentication
    const headers = {
      // Skip Content-Type for FormData (browser sets multipart boundary)
      ...(options.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...devImpersonationHeaders,
      ...options.headers,
    };

    /* Try to get Firebase ID token.
     *
     * NOT while impersonating. A browser that ever visited this app keeps an anonymous
     * Firebase session in IndexedDB, and it is restored ASYNCHRONOUSLY on load — so an
     * impersonated dev session silently became TWO identities: requests that went out before
     * the restore carried only X-Dev-User-ID, and everything after also carried the anon
     * token. The backend's DISABLE_AUTH branch lets a real token win over the header
     * (auth.py), so the second kind ran as the anonymous uid. Measured: the Workspace listed
     * the impersonated user's projects, then opening one 403'd and SessionView bounced
     * straight back — a card that "won't open", with nothing in the console.
     */
    const hasUser = !!(auth && auth.currentUser) && !DEV_IMPERSONATE_UID;
    if (hasUser) {
      try {
        // Force refresh on retry to get a new token
        const idToken = await auth.currentUser.getIdToken(isRetry);
        headers["Authorization"] = `Bearer ${idToken}`;
      } catch (tokenError) {
        console.warn("Could not get Firebase ID token:", tokenError.message);
        // If we can't get a token at all, user needs to re-login
        if (!isRetry) {
          this.emitSessionExpired();
          throw new Error("Session expired. Please sign in again.");
        }
      }
    }


    // Request timeout: caller override, else 120s for uploads (FormData), 30s for API calls
    const isUpload = options.body instanceof FormData;
    const timeoutMs = options.timeoutMs || (isUpload ? 120000 : 30000);

    // Our own options must not reach fetch(). `timeoutMs` and `force` are instructions to
    // THIS layer; passing them through is harmless today (fetch ignores unknown keys) and is
    // the kind of harmless that stops being harmless when a future option shares a name with
    // a real RequestInit field.
    const { timeoutMs: _t, force: _f, ...fetchOptions } = options;

    const method = (options.method || "GET").toUpperCase();
    // Only idempotent reads may be retried — see TRANSPORT_RETRIES. A retried POST could
    // apply the same mutation twice, because a request that failed on the way BACK was still
    // delivered.
    const retries = method === "GET" ? TRANSPORT_RETRIES : 0;

    let response;
    for (let attempt = 0; ; attempt += 1) {
      // A FRESH CONTROLLER PER ATTEMPT. An AbortController is single-use: once it has fired,
      // reusing it aborts the next request before it is sent, so the retry would "time out"
      // instantly and forever.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        response = await fetch(url, {
          ...fetchOptions,
          headers,
          credentials: "include",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        break;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          // A TIMEOUT IS NOT RETRIED. The 30s budget is already generous; spending another
          // 30 on a server that did not answer the first one turns a slow page into a frozen
          // one, and if it was slow because it is overloaded, a retry makes that worse.
          throw new Error("Request timed out. Please try again.");
        }
        if (!_isTransportError(fetchError) || attempt >= retries) {
          /* THE BROWSER'S WORDS ARE NOT A USEFUL ERROR. "Failed to fetch" names no endpoint,
             no cause and no remedy, and it is indistinguishable from a bug in the page — which
             is exactly how it was read for months. Say what actually happened, and say that it
             was already retried so nobody retries it by hand for no reason. */
          if (_isTransportError(fetchError)) {
            const e = new Error(
              `Can't reach the server (${endpoint.split("?")[0]}). `
              + `Tried ${retries + 1} time${retries ? "s" : ""} — it may be restarting.`,
            );
            e.transport = true;         // so a caller can tell "offline" from "rejected"
            e.cause = fetchError;
            throw e;
          }
          throw fetchError;
        }
        await _sleep(_backoff(attempt));
      }
    }

    if (!response.ok || response.status === 401) {
    }

    // Handle 401 Unauthorized - Token expired or invalid
    if (response.status === 401 && !isRetry) {
      console.log("🔄 Token expired, attempting automatic refresh...");

      // Try to refresh the token and retry the request ONCE
      if (auth && auth.currentUser) {
        try {
          // Force refresh the token
          await auth.currentUser.getIdToken(true);
          console.log("✅ Token refreshed, retrying request...");
          // Retry the request with the new token. _performRequest, not makeRequest: the
          // outer call is still in the in-flight map, so going back through the front door
          // would hand this retry its own unresolved promise.
          return this._performRequest(endpoint, options, true);
        } catch (refreshError) {
          console.error("❌ Token refresh failed:", refreshError.message);
          this.emitSessionExpired();
          throw new Error("Session expired. Please sign in again.");
        }
      } else {
        // No current user = no session to expire. Don't show the modal.
        throw new Error("Authentication required. Please sign in.");
      }
    }

    // Handle 401 on retry - refresh didn't help
    if (response.status === 401 && isRetry) {
      console.error("❌ Token still invalid after refresh");
      this.emitSessionExpired();
      throw new Error("Session expired. Please sign in again.");
    }

    if (!response.ok) {
      // FIXED: Better error handling for different response types
      let errorData;

      try {
        // Try to parse JSON error response
        errorData = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, create a basic error object
        errorData = {
          detail: `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
          statusText: response.statusText,
        };
      }

      // Create a structured error object
      const error = new Error();

      // Handle different error response formats
      if (errorData.detail) {
        // FastAPI style errors
        if (typeof errorData.detail === "string") {
          error.message = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Validation errors array
          error.message = errorData.detail
            .map((item) => {
              if (typeof item === "string") return item;
              if (item.msg)
                return `${item.loc ? item.loc.join(".") + ": " : ""}${item.msg}`;
              return JSON.stringify(item);
            })
            .join(", ");
        } else if (typeof errorData.detail === "object") {
          // Object with error details
          if (errorData.detail.message) {
            error.message = errorData.detail.message;
          } else {
            error.message = JSON.stringify(errorData.detail);
          }
        } else {
          error.message = String(errorData.detail);
        }
      } else if (errorData.message) {
        error.message = errorData.message;
      } else if (errorData.error) {
        error.message = errorData.error;
      } else {
        error.message = `HTTP ${response.status}: ${response.statusText}`;
      }

      // Attach the full error data for debugging
      error.status = response.status;
      error.statusText = response.statusText;
      error.detail = errorData.detail;
      error.responseData = errorData;

      console.error("API Error:", {
        url,
        status: response.status,
        statusText: response.statusText,
        errorData,
        formattedMessage: error.message,
      });

      throw error;
    }

    return response.json();
  }

  // Emit session expired event for the app to handle.
  //
  // IDLE MUST NOT PROMPT FOR SIGN-IN. This used to fire straight from any failed token refresh,
  // and the app is full of background polls (the generation queue, the study job list, the
  // player heartbeat) that keep hitting the API while nobody is touching the keyboard. A
  // sleeping laptop, a dropped wifi link or a single flaky refresh was therefore enough to
  // throw a sign-in prompt over an idle tab belonging to a user who was still signed in.
  //
  // So the event is now a CONCLUSION, not a symptom: we only dispatch after confirming with
  // Firebase that the session is genuinely gone. Async by design — callers fire and forget.
  async emitSessionExpired() {
    const AUTH_GRACE_PERIOD_MS = 5000;
    if (
      !this.authReady ||
      Date.now() - this.authReadyAt < AUTH_GRACE_PERIOD_MS
    ) {
      console.log(
        "⏳ Auth still initializing, suppressing session-expired event",
      );
      return;
    }

    // Offline is not signed out. The request failed because there is no network, and it will
    // succeed again on its own the moment there is.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      console.log("📡 Offline, suppressing session-expired event");
      return;
    }

    // Only one verification in flight, however many background polls failed at once.
    if (this._verifyingSession) return;
    this._verifyingSession = true;
    try {
      // Let the blip pass before asking. Firebase reports a null user for a moment during a
      // token refresh, and refreshes cluster exactly when a machine wakes up — which is
      // precisely the moment an idle tab was getting the prompt.
      await new Promise((resolve) => setTimeout(resolve, SESSION_RECHECK_DELAY_MS));

      const currentUser = auth && auth.currentUser;
      if (currentUser) {
        try {
          await currentUser.getIdToken(true);
          // The session is fine — it was a blip. Say nothing.
          console.log("✅ Session still valid, suppressing session-expired event");
          return;
        } catch (err) {
          // Genuinely unable to mint a token: the refresh token is dead. Fall through.
          console.warn("❌ Session confirmed expired:", err.message);
        }
      }

      // NO CURRENT USER MEANS NO SESSION TO EXPIRE. Without this, a 401 from a request that
      // was never about the session — a URL built with an undefined uid, say — reaches here,
      // finds no `currentUser` to re-verify against, skips the check above and fires anyway.
      // App.jsx answers by calling signOutUser() and opening the login gate, so a malformed
      // URL signs out a user whose token is perfectly good. Observed on production
      // 2026-09-22: POST /api/auth/user/undefined/analytics 401'd five times in ninety
      // minutes, and signing in on a phone bounced straight back to signed-out.
      //
      // The non-retry branch in _performRequest already reasons this way ("No current user =
      // no session to expire. Don't show the modal."); this is the same rule applied where it
      // was missing.
      if (!(auth && auth.currentUser)) {
        console.log("ℹ️ No current user — nothing to expire, suppressing");
        return;
      }

      console.log("🚨 Emitting session-expired event");
      window.dispatchEvent(
        new CustomEvent("session-expired", {
          detail: { message: "Your session has expired. Please sign in again." },
        }),
      );
    } finally {
      this._verifyingSession = false;
    }
  }

  // Health check
  async checkHealth() {
    try {
      // Prepare headers with authentication
      const headers = { ...devImpersonationHeaders };

      // Try to get Firebase ID token
      if (auth && auth.currentUser) {
        try {
          const idToken = await auth.currentUser.getIdToken();
          headers["Authorization"] = `Bearer ${idToken}`;
        } catch (tokenError) {
          console.warn(
            "⚠️ Could not get Firebase ID token for health check:",
            tokenError.message,
          );
        }
      }

      const response = await fetch(`${this.baseUrl}/health`, {
        headers,
        credentials: "include",
      });
      return {
        success: response.ok,
        data: response.ok ? await response.json() : null,
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Video generation endpoints
  async startVideoGeneration(videoRequest) {
    // Validate user_id
    if (!videoRequest.user_id) {
      throw new Error("User ID is required for video generation");
    }

    // FIXED: Ensure all numeric fields are proper types
    const cleanedRequest = {
      ...videoRequest,
      // Convert level to integer
      level: parseInt(videoRequest.level, 10) || 2,
      // Ensure boolean
      dry_run: Boolean(videoRequest.dry_run || videoRequest.dryRun),
    };

    // Validate level is in valid range
    if (cleanedRequest.level < 1 || cleanedRequest.level > 3) {
      cleanedRequest.level = 2; // Default to intermediate
    }

    console.log("Sending video generation request:", cleanedRequest);

    return this.makeRequest("/api/video/generate", {
      method: "POST",
      body: JSON.stringify(cleanedRequest),
    });
  }

  async getVideoStatus(jobId, options = {}) {
    return this.makeRequest(`/api/job/${jobId}/status`, options);
  }

  // Problem solving endpoints
  /* RUN A FAILED JOB AGAIN. The server rebuilds it from the request it was stored with —
     the card only knows a title and an error, and the thing that has to be re-run is the
     whole request. Returns a NEW job id; the old one stays failed. */
  async retryJob(jobId) {
    return this.makeRequest(`/api/video/retry/${jobId}`, { method: "POST" });
  }

  async startProblemSolving(problemRequest) {
    // Validate user_id
    if (!problemRequest.user_id) {
      throw new Error("User ID is required for problem solving");
    }

    // FIXED: Map to backend's expected field names
    const cleanedRequest = {
      problem: String(problemRequest.problem_text || ""), // Map problem_text to problem
      user_id: String(problemRequest.user_id),
      detail_level: parseInt(problemRequest.detail_level, 10) || 2,
      duration: parseInt(problemRequest.video_duration, 10) || 3, // Map video_duration to duration
      subject: String(problemRequest.subject || "math"),
      problem_type: String(problemRequest.problem_type || "homework"),
      show_work: problemRequest.show_work !== false,
      model: problemRequest.model || "mode_fast_37",
      is_global: false,
      dry_run: false,
    };

    // Validate detail_level is in valid range
    if (cleanedRequest.detail_level < 1 || cleanedRequest.detail_level > 3) {
      cleanedRequest.detail_level = 2; // Default to standard
    }

    // Validate duration is in valid range
    if (cleanedRequest.duration < 1 || cleanedRequest.duration > 10) {
      cleanedRequest.duration = 3; // Default duration
    }

    console.log("Sending problem solving request:", cleanedRequest);

    return this.makeRequest("/api/video/generate-solution", {
      method: "POST",
      body: JSON.stringify(cleanedRequest),
    });
  }

  // Unified video generation endpoint (works with or without auth)
  async startUnifiedGeneration(request) {
    const cleanedRequest = {
      text: String(request.text || ""),
      user_id: String(request.user_id || ""),
      model: request.model || "mode_fast_37",
      dry_run: Boolean(request.dry_run),
      is_global: Boolean(request.is_global),
    };

    // Forward content_type when present (e.g., "reel")
    if (request.content_type) {
      cleanedRequest.content_type = request.content_type;
    }

    // Forward parent_video_id for follow-up videos
    if (request.parent_video_id) {
      cleanedRequest.parent_video_id = request.parent_video_id;
    }

    // What to CALL the video, when that differs from what to generate it from. A follow-up's
    // `text` is a composite prompt carrying the parent problem, and the backend used to name
    // the video from it — so every follow-up off one problem inherited that problem's title.
    if (request.display_title) {
      cleanedRequest.display_title = String(request.display_title).slice(0, 300);
    }

    // Forward language for multilingual narration
    if (request.language) {
      cleanedRequest.language = request.language;
    }

    console.log("Sending unified generation request:", cleanedRequest);

    // Proof of work for anonymous visitors. A Firebase anonymous UID proves nothing — they are
    // handed out on request, so a script mints a fresh one per video and the daily counter never
    // bites. This makes each request cost CPU instead. Signed-in users skip it: they already
    // have an account behind them, and it is a per-account limit from there.
    const headers = {};
    const isAnon = auth?.currentUser?.isAnonymous;
    if (isAnon) {
      const proof = await getProofOfWork();
      // Deliberately not blocking on failure — the server ships in monitor-only and fails open,
      // so a browser that cannot solve it must not be a browser that cannot generate.
      if (proof) headers["X-Altcha-Proof"] = proof;
    }

    return this.makeRequest("/api/video/generate-unified", {
      method: "POST",
      headers,
      body: JSON.stringify(cleanedRequest),
    });
  }

  // Get anonymous user's video history (uses Firebase Anonymous Auth token)
  async getAnonymousVideos() {
    return this.makeRequest("/api/video/anonymous-videos");
  }

  // Unified batch video generation endpoint
  async startUnifiedBatchGeneration(request) {
    if (!request.user_id) {
      throw new Error("User ID is required for batch generation");
    }

    const cleanedRequest = {
      texts: request.texts,
      user_id: String(request.user_id),
      model: request.model || "mode_fast_37",
      dry_run: Boolean(request.dry_run),
      is_global: Boolean(request.is_global),
      ...(request.content_type && { content_type: request.content_type }),
    };

    console.log("Sending unified batch generation request:", cleanedRequest);

    return this.makeRequest("/api/video/generate-unified-batch", {
      method: "POST",
      body: JSON.stringify(cleanedRequest),
    });
  }

  // Video download
  getVideoDownloadUrl(jobId) {
    return `${this.baseUrl}/api/video/${jobId}`;
  }

  // Cancel a queued or processing job
  async cancelJob(jobId) {
    const user = auth.currentUser;
    const userId = user ? user.uid : DEV_IMPERSONATE_UID || "";
    return this.makeRequest(`/api/job/${jobId}?user_id=${userId}`, {
      method: "DELETE",
    });
  }

  // Same as makeRequest but returns the raw Response object (for blob/stream endpoints)
  async makeRawRequest(endpoint, options = {}, isRetry = false) {
    const url = `${this.baseUrl}${endpoint}`;

    const headers = {
      ...(options.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...devImpersonationHeaders,
      ...options.headers,
    };

    if (auth && auth.currentUser) {
      try {
        const idToken = await auth.currentUser.getIdToken(isRetry);
        headers["Authorization"] = `Bearer ${idToken}`;
      } catch (tokenError) {
        console.warn("⚠️ Could not get Firebase ID token:", tokenError.message);
        if (!isRetry) {
          this.emitSessionExpired();
          throw new Error("Session expired. Please sign in again.");
        }
      }
    }

    const config = { ...options, headers, credentials: "include" };
    const response = await fetch(url, config);

    if (response.status === 401 && !isRetry) {
      if (auth && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
          return this.makeRawRequest(endpoint, options, true);
        } catch (refreshError) {
          this.emitSessionExpired();
          throw new Error("Session expired. Please sign in again.");
        }
      } else {
        throw new Error("Authentication required. Please sign in.");
      }
    }

    if (response.status === 401 && isRetry) {
      this.emitSessionExpired();
      throw new Error("Session expired. Please sign in again.");
    }

    if (!response.ok) {
      const error = new Error(
        `HTTP ${response.status}: ${response.statusText}`,
      );
      error.status = response.status;
      throw error;
    }

    return response;
  }

  // ─── User Content ───
  // `q` was always supported by this endpoint — it IS /search — but no caller passed it, so
  // the Workspace filtered a progressively-loaded page cache in the browser instead.
  async getUserContent(userId, contentType, limit, offset = 0, q = "") {
    const params = new URLSearchParams({ content_type: contentType });
    if (limit) params.set("limit", limit);
    if (offset) params.set("offset", offset);
    if (q) params.set("q", q);
    return this.makeRequest(`/api/auth/user/${userId}/search?${params}`);
  }

  async seedStudyContent() {
    return this.makeRequest("/api/study/seed", { method: "POST" });
  }

  async saveVideoHistory(userId, data) {
    return this.makeRequest(`/api/user/${userId}/video-history`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async deleteVideo(videoId, userId, email) {
    const params = new URLSearchParams({ user_id: userId });
    if (email) params.set("user_email", email);
    return this.makeRequest(`/api/video/${videoId}?${params}`, {
      method: "DELETE",
    });
  }

  // ─── Engagement ───
  async getVideoEngagement(userId, videoId) {
    return this.makeRequest(
      `/api/auth/user/${userId}/video/${videoId}/engagement`,
    );
  }

  async getSuggestedVideos(userId, videoId, limit = 8, language = null) {
    let url = `/api/auth/user/${userId}/video/${videoId}/suggested?limit=${limit}`;
    if (language) url += `&language=${language}`;
    return this.makeRequest(url);
  }

  async getPublicSuggestedVideos(videoId, limit = 8, language = null) {
    let url = `${this.baseUrl}/api/public/videos/suggested/${videoId}?limit=${limit}`;
    if (language) url += `&language=${language}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async trackVideoView(videoId) {
    const url = `${this.baseUrl}/api/public/video/${videoId}/view`;
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  }

  async trackClick(jobId) {
    return this.makeRequest("/api/video/track-click", {
      method: "POST",
      body: JSON.stringify({ job_id: jobId }),
    });
  }

  async getExploreCurated() {
    const url = `${this.baseUrl}/api/public/explore/curated`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async getPublicCourse(courseId) {
    const url = `${this.baseUrl}/api/public/course/${encodeURIComponent(courseId)}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async searchPublicVideos(query, limit = 20, offset = 0, language = null) {
    let url = `${this.baseUrl}/api/public/explore/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`;
    if (language) url += `&language=${language}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Search failed: ${response.status}`);
    return response.json();
  }

  async getLandingStats() {
    const url = `${this.baseUrl}/api/public/landing-stats`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async getFeaturedReels() {
    const url = `${this.baseUrl}/api/public/featured-reels`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async getReferrerDetails(referrer_uid) {
    if (!referrer_uid) {
      throw new Error("referrer_uid is required");
    }

    const url = `${this.baseUrl}/api/public/get_referrer_details?referrer_uid=${encodeURIComponent(referrer_uid)}`;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Failed to get referrer: ${response.status}`);
    }

    return response.json();
  }

  async referralSuccessful(referrer_uid, referred_uid, referred_email) {
    if (!referrer_uid || !referred_uid || !referred_email) {
      throw new Error("Missing required referral fields");
    }

    const url =
      `${this.baseUrl}/api/public/referral_successful` +
      `?referrer_uid=${encodeURIComponent(referrer_uid)}` +
      `&referred_uid=${encodeURIComponent(referred_uid)}` +
      `&referred_email=${encodeURIComponent(referred_email)}`;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Referral failed: ${response.status}`);
    }

    return response.json();
  }

  // ─── Courses ───
  async getCourseDetail(courseId, userId) {
    const url = `${this.baseUrl}/api/course/${courseId}?user_id=${userId}`;
    const response = await fetch(url);
    if (!response.ok) {
      if (response.status === 404) throw new Error("Course not found");
      if (response.status === 403)
        throw new Error("Access denied to this course");
      throw new Error(
        `Failed to load course: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }

  async generateCourse(data) {
    return this.makeRequest("/api/generate-course", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async generateLesson(courseId, data) {
    return this.makeRequest(`/api/course/${courseId}/lesson/generate`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async generateAllLessons(courseId, data) {
    return this.makeRequest(`/api/course/${courseId}/lessons/generate-all`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getBillingHistory(userId) {
    return this.makeRequest(`/api/payment/billing-history?user_id=${userId}`);
  }

  async createPaymentSession(data) {
    return this.makeRequest("/api/payment/create-session", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getCreditPackages(options = {}) {
    return this.makeRequest("/api/payment/credit-packages", options);
  }

  async getSubscriptionPlans(options = {}) {
    return this.makeRequest("/api/payment/subscription-plans", options);
  }

  async createSubscription(data) {
    return this.makeRequest("/api/payment/create-subscription", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async manageSubscription(data) {
    return this.makeRequest("/api/payment/manage-subscription", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ─── Generation / PDF ───
  async extractPdfQuestions(formData) {
    return this.makeRequest("/api/pdf/extract-questions", {
      method: "POST",
      body: formData, // FormData — Content-Type auto-skipped
    });
  }

  async solvePdfQuestions(data) {
    return this.makeRequest("/api/pdf/solve-questions", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Goes through makeRequest, not raw fetch: the endpoint requires a bearer token
  // (media_router.download_video 401s without one), so a bare fetch always failed.
  async getVideoDownload(videoId) {
    return this.makeRequest(`/api/media/download/${videoId}`);
  }

  // ─── URL Builders (no fetch, return string) ───
  getVideoUrl(videoId) {
    return `${this.baseUrl}/api/media/video/${videoId}`;
  }

  getSubtitleUrl(videoId) {
    return `${this.baseUrl}/api/media/subtitles/${videoId}`;
  }

  getThumbnailUrl(videoId) {
    return `${this.baseUrl}/api/media/thumbnail/${videoId}`;
  }

  async getDebugJob(jobId) {
    return this.makeRequest(`/api/debug/job/${jobId}`);
  }

  async getRenderErrors(jobId) {
    return this.makeRequest(`/api/debug/render-errors/${jobId}`);
  }

  // ─── Public API keys (admin) ───
  async listApiKeys() {
    return this.makeRequest("/api/admin/api-keys");
  }

  async createApiKey(payload) {
    return this.makeRequest("/api/admin/api-keys", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async updateApiKeyLimits(keyId, limits) {
    return this.makeRequest(`/api/admin/api-keys/${keyId}/limits`, {
      method: "PATCH",
      body: JSON.stringify(limits),
    });
  }

  async revokeApiKey(keyId) {
    return this.makeRequest(`/api/admin/api-keys/${keyId}`, { method: "DELETE" });
  }

  async createApiKeyBillingLink(keyId) {
    return this.makeRequest(`/api/admin/api-keys/${keyId}/billing-link`, { method: "POST" });
  }

  // ─── Admin ───
  async getServiceAlerts(hours = 24) {
    return this.makeRequest(`/api/admin/service-alerts?hours=${hours}`);
  }

  // --- Container Lab: probe Modal container acquisition by hand -----------------
  async getContainerLabConfig() {
    return this.makeRequest(`/api/admin/container-lab/config`);
  }

  async getContainerLabFleet(appName) {
    return this.makeRequest(
      `/api/admin/container-lab/fleet?app_name=${encodeURIComponent(appName)}`);
  }

  async startContainerLabProbe(body) {
    return this.makeRequest(`/api/admin/container-lab/probe`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Poll target while a probe runs. The probe itself lives on Cloud Run; this only reads
  // a small Firestore document, so polling it costs the browser nothing.
  async getContainerLabProbe(probeId) {
    return this.makeRequest(`/api/admin/container-lab/probe/${probeId}`);
  }

  async listContainerLabProbes(limit = 25) {
    return this.makeRequest(`/api/admin/container-lab/probes?limit=${limit}`);
  }

  async getStripeRevenue(months = 12) {
    return this.makeRequest(`/api/admin/stripe-revenue?months=${months}`, { timeoutMs: 60000 });
  }

  // PDF uploaded -> project created -> videos generated, per project type. Server-cached.
  async getProjectFunnel(refresh = false) {
    return this.makeRequest(`/api/admin/project-funnel${refresh ? '?refresh=true' : ''}`,
      { timeoutMs: 60000 });
  }

  // Scans video_costs and users end to end, so it is slow and server-cached for 15 minutes.
  // The long timeout is for the cold call that actually does the scan.
  async getAcquisitionCohorts(refresh = false) {
    return this.makeRequest(`/api/admin/cohorts${refresh ? '?refresh=true' : ''}`,
      { timeoutMs: 120000 });
  }

  // Apple Pay / Google Pay / Link bought inside our modal. Sends an opaque confirmation token
  // — never card details, which Stripe's iframe keeps to itself.
  async startWalletSubscription(body) {
    return this.makeRequest('/api/payment/wallet-subscription', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getAdminDashboard(year, month) {
    // Heavy admin call — scans video_costs across all-time for costs + user metrics, so
    // the default 30s timeout was firing ("Request timed out"). Give it 120s like other
    // gather-everything admin calls.
    return this.makeRequest(`/api/admin/dashboard?year=${year}&month=${month}`, { timeoutMs: 120000 });
  }

  // maxJobsPerUserPerMinute is a WHAT-IF: it enforces nothing, it re-runs the window's real
  // jobs through that cap and returns the concurrency it would have left. Omitted from the
  // URL when unset so the response stays byte-identical to the plain call and any caching in
  // front of it is not split by an empty parameter.
  async getAdminAnalytics(days, maxJobsPerUserPerMinute = null) {
    const p = new URLSearchParams({ days: String(days) });
    if (maxJobsPerUserPerMinute > 0) {
      p.set('max_jobs_per_user_per_minute', String(maxJobsPerUserPerMinute));
    }
    return this.makeRequest(`/api/admin/analytics?${p.toString()}`);
  }

  async exportAdminCosts(year, month) {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (month) params.set("month", month);
    return this.makeRawRequest(`/api/admin/costs/export?${params}`);
  }

  async getAdminDurations(jobId) {
    return this.makeRequest(`/api/admin/durations/${jobId}`);
  }

  async getAdminProjects(limit = 100) {
    return this.makeRequest(`/api/admin/projects?limit=${limit}`);
  }

  async getAdminAnonUsers() {
    return this.makeRequest("/api/admin/anon-users");
  }

  // Anonymous free-video cap: the ceiling, today's usage, and the last 30 days' peak.
  async getFreeVideoCap() {
    return this.makeRequest("/api/admin/free-video-cap");
  }

  // ---- Site controls: the traffic gate, the stop buttons, the spend cap ----
  //
  // These are the only admin calls that must keep working while the site is down, which is
  // why /api/admin is on the middleware's always-open list. A control panel that goes down
  // with the thing it controls needs a deploy to undo a click.

  // `spend=false` skips the meter — three collection scans plus a billing read. The card
  // polls for queue depth far more often than spend figures move.
  async getSiteControls(spend = true) {
    return this.makeRequest(`/api/admin/site-controls?spend=${spend ? "true" : "false"}`);
  }

  // A PARTIAL update. The card saves one control at a time so a stale tab cannot reset a
  // switch someone else just flipped.
  async updateSiteControls(patch) {
    return this.makeRequest("/api/admin/site-controls", {
      method: "PUT",
      body: JSON.stringify(patch),
    });
  }

  // Separate from updateSiteControls on purpose: halting DRAINS the queue and restoring does
  // not, so they are two operations with different consequences rather than two values of
  // one field.
  async siteEmergencyShutdown(drain = true) {
    return this.makeRequest(`/api/admin/site-controls/shutdown?drain=${drain ? "true" : "false"}`,
                            { method: "POST" });
  }

  async siteRestore() {
    return this.makeRequest("/api/admin/site-controls/restore", { method: "POST" });
  }

  // PUBLIC and unauthenticated — the app reads this to find out it should show the
  // maintenance notice instead of itself.
  async getSiteStatus() {
    return this.makeRequest("/api/site-status");
  }

  async getWatchStats(days = 30) {
    return this.makeRequest(
      `/api/admin/user-behavior/watch-stats?days=${days}`,
    );
  }

  async backfillAnonUsers() {
    return this.makeRequest("/api/admin/anon-users/backfill", {
      method: "POST",
    });
  }

  async getUserAnalytics() {
    return this.makeRequest("/api/admin/user-analytics");
  }

  async getFunnelAnalytics(options = {}) {
    const params = new URLSearchParams();
    if (options.includeAnonymous) {
      params.set("include_anonymous", "true");
    }
    const query = params.toString();
    return this.makeRequest(`/api/admin/funnel-analytics${query ? `?${query}` : ""}`);
  }

  // ─── Admin Reel Generator ───
  /** Every prompt package a reel can be written with — the corpus folders. */
  async getPromptPackages() {
    return this.makeRequest("/api/admin/prompt-packages");
  }

  async getReelPrompts() {
    return this.makeRequest("/api/admin/reel-prompts");
  }

  async getAdminReels(limit = 50, offset = 0, bust = false) {
    return this.makeRequest(`/api/admin/reels?limit=${limit}&offset=${offset}${bust ? '&bust=true' : ''}`);
  }

  async toggleReelPin(videoId) {
    return this.makeRequest(`/api/admin/reels/${videoId}/pin`, {
      method: "PATCH",
    });
  }

  async generateAdminReel(data) {
    return this.makeRequest("/api/admin/generate-reel", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /** One image from a prompt's image slot, as an object URL.
   *
   * NOT an <img src>. The admin endpoints authenticate with a Bearer token and a browser
   * image request carries no Authorization header, so a direct src would 401 and the grid
   * would render as broken tiles with nothing in the console to explain it. The caller owns
   * the returned URL and must revokeObjectURL it.
   */
  async getPromptImageUrl(stage, key, idx, corpus) {
    const headers = {};
    if (auth && auth.currentUser) {
      headers["Authorization"] = `Bearer ${await auth.currentUser.getIdToken()}`;
    }
    const q = `?corpus=${encodeURIComponent(corpus || "")}`;
    const res = await fetch(
      `${this.baseUrl}/api/admin/prompt-image/${stage}/${key}/${idx}${q}`, { headers });
    if (!res.ok) throw new Error(`image ${idx}: ${res.status}`);
    return URL.createObjectURL(await res.blob());
  }

  async getPromptText(stage, key, corpus) {
    const q = corpus ? `?corpus=${encodeURIComponent(corpus)}` : '';
    return this.makeRequest(`/api/admin/prompt-text/${stage}/${key}${q}`);
  }

  async getPromptLayout(stage, corpus, secondReel = false, refineFps = 0, images = 0) {
    const p = new URLSearchParams();
    if (corpus) p.set('corpus', corpus);
    if (secondReel) p.set('second_reel', 'true');
    // The reviser's frame count follows the fps picker, so the viewer has to know it or it
    // reports a group size the launch will not send.
    if (refineFps) p.set('refine_fps', String(refineFps));
    // How many frames the page has attached: `{scene_frames}` is a real slot, and the
    // panel has no render of its own to grab stills from.
    if (images) p.set('images', String(images));
    const q = p.toString();
    return this.makeRequest(`/api/admin/prompt-layout/${stage}${q ? `?${q}` : ''}`);
  }

  /** What happens to the scene code AFTER the model answers — the injected `safe_*`
   *  helpers, the ManimCE→manimx rewrite table, and which of those steps actually run for
   *  this product. The other half of a codegen stage: /prompt-layout says what is SENT.
   */
  async getCodegenHelpers(product = 'reel', pipelinePreset = '', frameLayout = 'SINGLE') {
    const p = new URLSearchParams({ product });
    if (pipelinePreset) p.set('pipeline_preset', pipelinePreset);
    if (frameLayout) p.set('frame_layout', frameLayout);
    return this.makeRequest(`/api/admin/codegen-helpers?${p.toString()}`);
  }

  /** Write one text block back to its corpus. Only blocks the assembly marks editable. */
  // `template` and `documents` carry the OTHER half of what the panel is holding, so one
  // save writes everything into one fork. Without them each button forked from disk and
  // silently discarded whatever the other one had pending.
  // `save` is { mode: 'fork' | 'overwrite', save_as?: 'name' }. Omitted means fork, which
  // is what every save did before the dialog existed — so an old caller stays
  // non-destructive.
  async savePromptText(stage, key, corpus, text, template = null, save = null) {
    return this.makeRequest(`/api/admin/prompt-text/${stage}/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ corpus, text, template, ...(save || {}) }),
    });
  }

  /** `pacing` is the lesson pacing numbers the panel is holding, or null for "this save is
   *  not about them". They ride on the layout save because they live in the same
   *  `prompt_layout.json` and fork for the same reason — a window and a scene count change
   *  the bytes sent as surely as retyping a sentence does. */
  async savePromptLayout(stage, corpus, template, documents = null, save = null,
                         pacing = null, options = null) {
    return this.makeRequest(`/api/admin/prompt-layout/${stage}`, {
      method: 'PUT',
      body: JSON.stringify({ corpus, template, documents, pacing, options,
                             ...(save || {}) }),
    });
  }

  // RENAME A DOCUMENT YOU ADDED, and the slot that places it, together. `template` is what
  // the editor is holding — it may carry edits not yet saved, and the slot has to be renamed
  // in THAT text, not in the stored copy.
  async renamePromptDoc(corpus, key, name, template = null) {
    return this.makeRequest("/api/admin/prompt-rename-doc", {
      method: "POST",
      body: JSON.stringify({ corpus, key, name, template }),
    });
  }

  // RENAME WHAT A CHIP IS CALLED — its label, not its key. The key is read by name in
  // Python and written into every saved layout; the label is nobody's dependency. Pass an
  // empty label to put the shipped one back.
  async renamePromptChip(corpus, key, label, stage = "codegen") {
    return this.makeRequest("/api/admin/prompt-rename-chip", {
      method: "POST",
      body: JSON.stringify({ corpus, stage, key, label }),
    });
  }

  // ADD A DOCUMENT OF THIS CORPUS'S OWN. It becomes the slot {key}, optional, and goes
  // nowhere until the layout places it — so creating one cannot change a prompt by itself.
  async createPromptDoc(corpus, name, text) {
    return this.makeRequest("/api/admin/prompt-doc", {
      method: "POST",
      body: JSON.stringify({ corpus, name, text: text || "" }),
    });
  }

  // RE-READ THE CORPUS FROM DISK. The server caches a corpus the first time it reads one, so
  // a file edited outside the app — in an editor, by a script, by pulling a branch — stays
  // invisible for the life of the process and the panel shows a document that is no longer
  // there. Saving from this page already clears those caches; this is the same clear without
  // having to write something first.
  async reloadPrompts(corpus) {
    const p = new URLSearchParams();
    if (corpus) p.set("corpus", corpus);
    const q = p.toString();
    return this.makeRequest(`/api/admin/prompt-reload${q ? `?${q}` : ""}`, { method: "POST" });
  }

  async getJobTimeline(jobId) {
    return this.makeRequest(`/api/admin/job-timeline/${jobId}`);
  }

  /** One package's three editable documents — narration, code prompt, examples — resolved
   *  the way the pipeline resolves them, plus per-document `*_is_own` flags saying whether
   *  the package supplies it or inherits the shared one.
   *
   *  REPLACED getSavedPrompts/savePrompt/deleteSavedPrompt. Those talked to
   *  `/api/admin/saved-prompts`: seven presets hand-written in Python over a Firestore
   *  collection that held zero rows, and a save that wrote a four-file folder while dropping
   *  the other fourteen. A preset is a package now; `getPromptPackages` is the list and this
   *  is the text of whichever one is selected. */
  /** Pin which package a reel is made with when nobody picks one — or clear it with null.
   *  SERVER-SIDE, not localStorage: the pipeline reads the same answer for a job that named
   *  no package, so a pin only this browser knew about would put the picker and the pipeline
   *  back into disagreement. */
  /** What a reel starts with: the package, the counts and the frame rates. */
  async getReelDefaults() {
    return this.makeRequest("/api/admin/reel-defaults");
  }

  /** Pin one or more run settings. A key set to null unpins it to its shipped value.
   *  A PATCH — the stars are pressed one at a time, so a call that replaced the whole set
   *  would mean pinning the takes silently un-pinned the drafts. */
  /** Copy a package into another video type + run, as a package of its own.
   *  `action` is the run to copy into; `product` defaults to the source's own type, which is
   *  the only legal answer for a package that carries one type's documents. */
  async copyPromptPackage(name, action, product = null) {
    return this.makeRequest(`/api/admin/prompt-packages/${encodeURIComponent(name)}/copy`, {
      method: "POST",
      body: JSON.stringify({ action, product }),
    });
  }

  async pinRunDefaults(patch) {
    return this.makeRequest("/api/admin/reel-defaults", {
      method: "PUT",
      body: JSON.stringify({ run: patch }),
    });
  }

  /** Set the package PRODUCTION is made with when a job picks none — or clear it with null.
   *  `product` is "reel" or "lesson"; lessons and solutions share one default, because
   *  `settings.corpus_for` tells them apart by duration_type and that is "reel" or not.
   *  Read by the pipeline through `gen_videos/prompt_defaults`, so it takes effect everywhere
   *  within PROMPT_DEFAULT_TTL_SECONDS and needs no deploy. */
  /*  ONE PIN PER RUN TYPE, AND THE PACKAGE NAMES ITS OWN RENDERER. `renderer` defaulted to
   *  "manimx" here, which meant the Workshop always SENT one — so pinning a ManimCE package
   *  was refused by the server's mismatch guard unless the renderer toggle happened to be on
   *  the matching chip. Omitted, the server reads the package's own `renderer.txt`, which is
   *  the file the pipeline reads. Pass one explicitly only to assert it and be told if it
   *  disagrees. */
  async pinDefaultPackage(name, product = "reel", renderer = null) {
    return this.makeRequest("/api/admin/prompt-packages/default", {
      method: "PUT",
      body: JSON.stringify({ corpus: name || null, product,
                             ...(renderer ? { renderer } : {}) }),
    });
  }

  /** Mark which renderer a package is written for — "manimx" or "manimce".
   *  The Workshop filters its package list on this, and `pinDefaultPackage` refuses a
   *  mismatch against it, so a fork has to be markable from the same screen rather than by
   *  editing a file in the repo and redeploying. */
  async setPackageRenderer(name, renderer) {
    return this.makeRequest("/api/admin/prompt-packages/renderer", {
      method: "PUT",
      body: JSON.stringify({ corpus: name, renderer }),
    });
  }

  async getPackageDocuments(name) {
    return this.makeRequest(
      `/api/admin/prompt-packages/${encodeURIComponent(name)}/documents`);
  }

  // ─── Social Publishing (Buffer) ───
  async getSocialChannels() {
    return this.makeRequest("/api/admin/social/channels");
  }

  async getScheduledPosts() {
    return this.makeRequest("/api/admin/social/scheduled");
  }

  async publishToSocial(videoId, data) {
    return this.makeRequest(`/api/admin/social/publish/${videoId}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Measures what each published post actually went out as — frame size, fps, codec, bitrate —
  // by probing the file and caching the answer on the video. Nothing in the pipeline records
  // this, so the only source is the file itself; already-measured videos are skipped, which is
  // why pressing it twice is cheap. Long timeout: it is one network probe per video.
  async measurePostSpecs() {
    return this.makeRequest("/api/admin/social/measure-specs",
      { method: "POST", timeoutMs: 180000 });
  }

  // ─── The story. Instagram only, and a different surface from the feed post: no caption,
  // no tracked link (Buffer exposes no link-sticker input), gone in 24 hours. The source is
  // either one of our reels, by video_id, or a clip uploaded here, by asset_key.
  async getStoryAssets() {
    return this.makeRequest("/api/admin/social/story-assets");
  }

  async uploadStoryAsset(file, name = "") {
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("name", name);
    return this.makeRequest("/api/admin/social/story-assets", {
      method: "POST",
      body: form,          // FormData — Content-Type auto-skipped
      timeoutMs: 180000,   // a clip, over a home connection
    });
  }

  async deleteStoryAsset(key) {
    return this.makeRequest(`/api/admin/social/story-assets/${encodeURIComponent(key)}`, {
      method: "DELETE",
    });
  }

  async publishStory(data) {
    return this.makeRequest("/api/admin/social/story", {
      method: "POST",
      body: JSON.stringify(data),
      timeoutMs: 60000,    // Buffer fetches the file before it answers
    });
  }

  async deleteScheduledPost(postId, service) {
    const qs = service ? `?service=${encodeURIComponent(service)}` : "";
    return this.makeRequest(`/api/admin/social/scheduled/${postId}${qs}`, {
      method: "DELETE",
    });
  }

  // Every published post with its metrics — one row per (reel, channel).
  // 60s, not the default 30s: the cold call walks every reel before the 60s server cache is
  // warm, and timing that out leaves the tab showing an error instead of the table.
  async getPublishedPosts() {
    return this.makeRequest("/api/admin/social/posts", { timeoutMs: 60000 });
  }

  // Pulls view counts (the CTR denominator) from YouTube's Data API and Buffer. Clicks are
  // never refreshed here — /r/<code> counts those as they happen.
  //
  // Long timeout: this is two external APIs plus a write per updated post, and the default
  // 30s is a limit on our own backend rather than on YouTube and Buffer answering.
  async refreshSocialMetrics() {
    return this.makeRequest("/api/admin/social/refresh-metrics",
      { method: "POST", timeoutMs: 120000 });
  }

  // ─── Email Campaigns (Brevo) ───
  // ── Manual reel editor ──────────────────────────────────────────────────
  async getReelForEditing(jobId) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}`);
  }

  // Three ways to change one scene's picture, all leaving its audio and timing alone:
  //   mode "fix"   — revise the code that is there, against your note
  //   mode "fresh" — write the scene again from its own description
  //   code         — put a take you already have back in; no model runs
  // `editReel`, `workshopScript` and `workshopNewScript` lived here and are gone (2026-09-30)
  // with the Quick edit panel. Editing a reel goes through `editReelScene` / `editReelScript`
  // below, which drive the real pipeline instead of a second copy of it.

  // NO REFINE. `refineRounds`, `judgeModel` and `judgeFps` are gone: a refine round is
  // render -> Sonnet watches the clip -> Sonnet rewrites the scene, four times by default,
  // and it is the single most expensive thing a reel can do ($6.79 measured on one job that
  // then parked and failed, against a $0.19 median). Editor actions draw the scene once.
  // The endpoint refuses `mode: "refine"` outright, so sending it from here would only turn
  // a working button into a 400.
  /** What the coder WOULD do about your note, before it does any of it.
   *
   * The same prompt the fix sends, with one instruction swapped at the end — so the plan
   * you read is a reading of the real request. Nothing is queued and nothing renders.
   * Approving it means passing it back to `editReelScene` as `plan`, which is what holds
   * the edit to the scope you agreed.
   */
  /** Put a package in the picker's "Show all", or take it out.
   *  A star is how a package earns a place in the longer list — nothing is in it by accident.
   *  Distinct from PINNING, which makes a package the default for reels that pick nothing. */
  async starPromptPackage(name, starred) {
    return this.makeRequest(`/api/admin/prompt-packages/${encodeURIComponent(name)}/star`, {
      method: "PUT",
      body: JSON.stringify({ starred: !!starred }),
    });
  }

  /** Rename a package — the folder AND every record that keys on its name.
   *  Refused when the target exists, when it is the shipped package, and when the new name
   *  would leave a run with no package (the run suffix is part of the name).
   *  Past reels keep naming the OLD package in their stored settings: those strings are
   *  history and are deliberately not rewritten. */
  async renamePromptPackage(name, next) {
    return this.makeRequest(`/api/admin/prompt-packages/${encodeURIComponent(name)}/rename`, {
      method: "PUT",
      body: JSON.stringify({ name: next }),
    });
  }

  /** Delete a package from the pickers, or put it back.
   *  NOTHING IS ERASED: the folder stays on disk and the stored copy stays in Firestore, so a
   *  job that carries the name still runs it. It stops being offered and stops being counted.
   *  Pass `hidden: false` to restore one — `getPromptPackages().deleted` is the list. */
  async hidePromptPackage(name, hidden = true) {
    return this.makeRequest(`/api/admin/prompt-packages/${encodeURIComponent(name)}/hidden`, {
      method: "PUT",
      body: JSON.stringify({ hidden: !!hidden }),
    });
  }

  async planReelScene(jobId, index, instruction,
                      { mode = "fix", model, corpus, images, secondReel,
                        previous, comment } = {}) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/scene/${index}/plan`, {
      method: "POST",
      // `previous` + `comment` make it a REVISION of the plan on screen rather than another
      // roll of the same question. Sent together or not at all.
      body: JSON.stringify({ instruction, mode, model, corpus, images,
                             second_reel: secondReel, previous, comment }),
    });
  }

  async editReelScene(jobId, index, instruction, model, quantity = 1,
                      { mode = "fix", code, images, outro, corpus, secondReel,
                        plan } = {}) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/scene/${index}/edit`, {
      method: "POST",
      // `images` is [{url, label}] — data URLs of the frames the note is about, each labelled
      // with the moment it was taken at. They are uploaded server-side and reach the coder as
      // pictures, so "the thing at the end is unreadable" is a sentence about something it
      // can see, and several of them describe a movement rather than a moment.
      //
      body: JSON.stringify({ instruction, model, quantity, mode, code, images, outro, corpus,
        // undefined = inherit whatever this reel was generated with
        second_reel: secondReel,
        // Set only when the edit was planned first; absent is an ordinary unplanned fix.
        plan }),
    });
  }

  // Exactly what a Fix or a Redraw will put in front of the coder, before you press it —
  // the scene description, your note, and the code being replaced, assembled by the same
  // function the pipeline calls.
  async getSceneBrief(jobId, index, mode, note, images = 0, corpus) {
    // `images` is how many frames the page has attached — it decides whether the brief
    // writes the line that introduces them, so the preview matches what the button sends.
    const q = new URLSearchParams({ mode: mode || "fix", note: note || "",
                                    images: String(images || 0),
                                    ...(corpus ? { corpus } : {}) });
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/scene/${index}/brief?${q}`);
  }

  // The takes every scene was chosen from, still playable — rendered and paid for during
  // generation, and until now pointed at by nothing once the pick was made.
  async getReelSceneTakes(jobId) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/takes`);
  }

  // EVERY READING THIS SCENE HAS EVER HAD, still playable. A re-record does not replace a
  // recording, it adds one — the old read stays in storage under the old leg's id — so these
  // are the takes you can go back to instead of re-rolling the dice on the voice.
  async getReelAudioTakes(jobId, index) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/scene/${index}/audio-takes`);
  }

  // Put one of them back. No TTS and no codegen: the recording is pinned and the one scene
  // whose voice changed is re-rendered, with its animation re-timed to the new reading.
  // `fromJob` is the leg the recording came from — the server looks the URL up itself, so a
  // URL can never be pinned into a reel's audio from the outside.
  async useReelAudioTake(jobId, index, fromJob) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/scene/${index}/audio-take`, {
      method: "POST",
      body: JSON.stringify({ from_job: fromJob }),
    });
  }

  // ── the cut: swapping a take without making a reel ──────────────────────
  // A reel IS a sequence of clips, concatenated at the end. Choosing a different take for one
  // scene changes one of those clips, and used to cost a queued job that re-rendered what it
  // already had to produce a file differing in eleven seconds. These three say it instead:
  // `setReelCut` records the swap (the player applies it live), `assembleReelCut` is the one
  // call that builds a file, and publishing is the only thing that needs one.
  // `scenes` is {sceneIndex: {url, code, from_job, take}}; a blank url restores that scene.
  async setReelCut(jobId, scenes) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/cut`, {
      method: "PUT",
      body: JSON.stringify({ scenes }),
    });
  }

  async clearReelCut(jobId) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/cut`, { method: "DELETE" });
  }

  async assembleReelCut(jobId) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/assemble`, { method: "POST" });
  }

  // New words for one scene, or for several at once. `keepVisuals` redraws nothing — the
  // words did not change, so it is a fresh recording with every animation refitted to it.
  // `rewriteAll` redraws every scene. The default is neither: the scenes whose words changed
  // are drawn again (from the code that is there) and the rest are kept and refitted.
  //
  // `lines` is {scene index: new line} and scopes the rewrite to more than one scene; `index`
  // stays in the URL as the scene the page was on. Pass `narration` instead for the
  // single-scene form — the server accepts both and `lines` wins.
  async editReelScript(jobId, index, narration, model, quantity = 1,
                       { keepVisuals = false, rewriteAll = false, wholeReel = false,
                         outro, lines } = {}) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/scene/${index}/script`, {
      method: "POST",
      body: JSON.stringify({ narration, model, quantity, keep_visuals: keepVisuals,
                             rewrite_all: rewriteAll, whole_reel: wholeReel, outro,
                             ...(lines ? { lines } : {}) }),
    });
  }

  /** Talk about ONE scene's words, with the solver's working and the whole script in hand.
   *  The context is assembled SERVER-SIDE from the reel's own documents — see `_script_context`
   *  — so this sends only the conversation and whatever is typed in the box. Nothing is
   *  queued and nothing is written: `suggestion` is a line you may choose to put in the box.
   *  @param draft the unsaved line on screen, so the reply is about what you are looking at. */
  async chatAboutReelScript(jobId, index, message, { history = [], draft, model } = {}) {
    return this.makeRequest(
      `/api/admin/reel-editor/${jobId}/scene/${index}/script-chat`, {
        method: "POST",
        body: JSON.stringify({ message, history, draft, model }),
      });
  }

  /** How many runs of each video type this account has made — the number on the type chips.
   *
   *  A COUNT AGGREGATION server-side, so "733 reels" costs one read rather than 733. It
   *  replaced the package count, which was the same fact twice: the packages serving a type
   *  are the chips two rows below it.
   */
  async getLabRunCounts() {
    return this.makeRequest("/api/admin/prompt-lab/run-counts");
  }

  /** Rate the video a lab run produced, out of five. `stars: 0` takes the rating back.
   *
   *  WHICH PACKAGE IT CREDITS IS READ FROM THE JOB, server-side — the picker may have moved
   *  since the run, and a star credited to whatever is selected now would be worse than no
   *  star. So this sends the job id and the number, and nothing else.
   *
   *  The response carries the package's new average, and `getPromptPackages().ratings` is
   *  where every picker reads it from.
   */
  async rateLabRun(jobId, stars) {
    return this.makeRequest("/api/admin/prompt-lab/rating", {
      method: "PUT",
      body: JSON.stringify({ job_id: jobId, stars }),
    });
  }

  /* ── TEST SETS: the same topics, two packages, side by side ────────────────────────────
   *
   *  THE SAME TOPICS ON BOTH SIDES IS THE WHOLE POINT. A set is topics × packages, so every
   *  video has a counterpart differing in exactly one thing; comparing two runs about two
   *  different subjects compares the subjects. The server launches each arm through the
   *  probe's own pinned shape, so neither arm is a different machine.
   */

  /** Launch every (topic × package) run of one comparison. Returns the set with its jobs. */
  // `model` is ONE model for the whole set — it writes the narration, the scene code and
  // the brainstorm on every arm. Omitted means the server's default; the server refuses
  // anything that is not a model it can run on, by name.
  async createTestSet({ product = 'reel', topics, corpora, segments, label, model,
                        inject_helpers, style } = {}) {
    return this.makeRequest("/api/admin/prompt-test-sets", {
      method: "POST",
      body: JSON.stringify({ product, topics, corpora, segments, label, model,
                             inject_helpers, style }),
    });
  }

  /** This account's recent sets — enough to pick one from, not to draw it. */
  async getTestSets(product = "") {
    const q = product ? `?product=${encodeURIComponent(product)}` : "";
    return this.makeRequest(`/api/admin/prompt-test-sets${q}`);
  }

  /** One set, with every run's live state, its video and its star.
   *
   *  THREE BATCHED READS SERVER-SIDE, not a status call per run: ten of those on a poll is
   *  how this project exhausted its daily Firestore read quota once already.
   */
  async getTestSet(setId) {
    return this.makeRequest(
      `/api/admin/prompt-test-sets/${encodeURIComponent(setId)}`);
  }

  /** Forget a set. The videos it made stay in the library. */
  async deleteTestSet(setId) {
    return this.makeRequest(
      `/api/admin/prompt-test-sets/${encodeURIComponent(setId)}`, { method: "DELETE" });
  }

  /* Forget ONE run, or every failed run, without losing the rest of the comparison.
     The job and anything it produced are untouched — this drops the row only. */
  async dropTestSetRuns(setId, { jobId = "", failedOnly = false } = {}) {
    const q = jobId ? `job_id=${encodeURIComponent(jobId)}`
                    : `failed_only=${failedOnly ? "true" : "false"}`;
    return this.makeRequest(
      `/api/admin/prompt-test-sets/${encodeURIComponent(setId)}/runs?${q}`,
      { method: "DELETE" });
  }

  // Priced before anything is queued, per model, so each button has a number beside it.
  //
  // NOT `getReelCostEstimate`. That name belongs to the composer's whole-run estimator two
  // hundred lines below, and when both had it the later definition simply replaced this one
  // — every price on the editor read "COST UNKNOWN" while the request went to the other
  // endpoint with a job id where a query object should have been.
  // HOW THE CODER WILL READ YOUR NOTE — add, change, remove or rework. Classified on the
  // server by the same pass that frames the prompt, so the editor cannot show one reading
  // while the coder is sent another. Cheap by design: the full prompt preview is 68,000
  // characters and cannot be fetched while someone types.
  async readReelNote(note) {
    return this.makeRequest(`/api/admin/reel-editor/read-note`, {
      method: "POST",
      body: JSON.stringify({ note: note || "" }),
    });
  }

  async getReelActionEstimate(jobId) {
    return this.makeRequest(`/api/admin/reel-editor/${jobId}/estimate`);
  }

  // The endings a reel can be given — the shipped wizard, anything in assets/outros, and
  // anything uploaded from the ad screen. Each entry carries a `url` a <video> can play.
  async getReelOutros() {
    return this.makeRequest("/api/admin/reel-editor/new/outros");
  }

  // Add an ending by uploading the clip. The bytes go to Storage, not to the container's
  // disk, so a worker on another machine can reach it when the reel is assembled.
  async uploadReelOutro(file, name = "") {
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("name", name);
    return this.makeRequest("/api/admin/reel-editor/new/outros", {
      method: "POST",
      body: form, // FormData — Content-Type auto-skipped
    });
  }

  // Only an upload can be removed: the shipped clip and assets/outros are in the image.
  async deleteReelOutro(key) {
    return this.makeRequest(`/api/admin/reel-editor/new/outros/${encodeURIComponent(key)}`, {
      method: "DELETE",
    });
  }

  // Where the ad screen plays an ending from. Relative paths come back from the server for
  // the clips that live inside the container; they need this origin in front of them.
  reelOutroUrl(url) {
    return url && url.startsWith("/") ? `${this.baseUrl}${url}` : url;
  }

  // THE WHOLE ENDING AS ONE FILE. `spec` is the clip keys joined by "+", the same string the
  // server stores on the reel. Built and cached by the backend, conformed exactly the way the
  // worker joins them — so the preview is the thing that ships, not an impression of it.
  reelOutroSequenceUrl(spec) {
    return `${this.baseUrl}/api/admin/reel-editor/new/outro-sequence/${encodeURIComponent(spec)}`;
  }

  // A STILL FROM AN ENDING, so a tile can show what the clip is without loading the clip. The
  // ad screen draws nine of these; as <video> that was nine media connections against a host a
  // browser gives six, and the player — which asks last — got none.
  reelOutroPosterUrl(key) {
    return `${this.baseUrl}/api/admin/reel-editor/new/outro-poster/${encodeURIComponent(key)}`;
  }

  // The reel's video, served from OUR origin rather than storage.googleapis.com. Returns a
  // URL rather than fetching: it goes straight into a <video src>, which cannot carry an
  // Authorization header anyway.
  reelVideoUrl(jobId) {
    return `${this.baseUrl}/api/admin/reel-video/${jobId}`;
  }

  async getReelJobStatus(jobId) {
    return this.makeRequest(`/api/admin/reel-editor/status/${jobId}`);
  }

  // ── YouTube channel automation ──────────────────────────────────────────
  async getYouTubeStatus() {
    return this.makeRequest("/api/admin/youtube/status");
  }

  /** Everything that reached the channel, newest first. Separate from getYouTubeStatus
   *  because that one is POLLED and the back catalogue does not change between two polls —
   *  and because /status returns the newest 30 queue rows of any state, which with ideas and
   *  retries interleaved left most published videos off the page entirely. */
  async getYouTubePosted(limit = 60) {
    return this.makeRequest(`/api/admin/youtube/posted?limit=${limit}`);
  }

  // Separate from getYouTubeStatus on purpose: this one reaches YouTube's API, so a slow or
  // rate-limited response must not hold up the rest of the admin page.
  // Clicks per channel, counted on our own server as each link redirects. Distinct from
  // getAcquisitionCohorts, which is about retention over time rather than where people came
  // from — same word, different question. Counted signups until 2026-10-29; see
  // AcquisitionStrip for why that was the wrong end of the funnel.
  async getClicksBySource() {
    return this.makeRequest("/api/admin/acquisition");
  }

  async getYouTubeEngagement(refresh = false) {
    return this.makeRequest(`/api/admin/youtube/engagement${refresh ? "?refresh=true" : ""}`);
  }

  async setYouTubeConfig(patch) {
    return this.makeRequest("/api/admin/youtube/config", {
      method: "POST",
      body: JSON.stringify(patch),
    });
  }

  async runYouTubeNow() {
    return this.makeRequest("/api/admin/youtube/run", { method: "POST" });
  }

  async publishYouTubeItem(itemId) {
    return this.makeRequest(`/api/admin/youtube/publish/${itemId}`, { method: "POST" });
  }

  async dropYouTubeItem(itemId) {
    return this.makeRequest(`/api/admin/youtube/queue/${itemId}`, { method: "DELETE" });
  }

  // Regenerate a FAILED queue item on the same title, reusing its row. Not a delete-and-
  // remake: the title is already claimed in the ledger, so remaking it as a new idea would
  // be refused as a duplicate.
  async retryYouTubeItem(itemId) {
    return this.makeRequest(`/api/admin/youtube/retry/${itemId}`, { method: "POST" });
  }

  // Move every failed title to `pending`. Starts nothing — the hourly tick drains the backlog
  // one per slot, so this cannot bypass the one-at-a-time or per-day limits.
  async requeueFailedYouTube() {
    return this.makeRequest("/api/admin/youtube/requeue-failed", { method: "POST" });
  }

  async getEmailAudience() {
    return this.makeRequest("/api/admin/campaigns/audience");
  }

  async syncEmailContacts() {
    return this.makeRequest("/api/admin/campaigns/sync", {
      method: "POST",
    });
  }

  async getSendAllowance() {
    return this.makeRequest("/api/admin/campaigns/send-allowance");
  }

  async getWeeklyDigest() {
    return this.makeRequest("/api/admin/campaigns/weekly-digest");
  }

  async setWeeklyDigest(enabled) {
    return this.makeRequest("/api/admin/campaigns/weekly-digest", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
  }

  async getEmailCampaigns(limit = 50) {
    return this.makeRequest(`/api/admin/campaigns?limit=${limit}`);
  }

  async createEmailCampaign(data) {
    return this.makeRequest("/api/admin/campaigns", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async sendEmailTest(campaignId) {
    return this.makeRequest(`/api/admin/campaigns/${campaignId}/test`, { method: "POST" });
  }

  async sendEmailCampaign(campaignId, data) {
    return this.makeRequest(`/api/admin/campaigns/${campaignId}/send`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Transactional purchase receipts. Not campaigns — they fire automatically off Stripe's
  // invoice.paid — but they surface on the same page so all outbound email has one home.
  async getDunning() {
    return this.makeRequest("/api/admin/campaigns/dunning");
  }

  async setDunning(enabled) {
    return this.makeRequest("/api/admin/campaigns/dunning", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    });
  }

  async getEmailReceipts(limit = 50) {
    return this.makeRequest(`/api/admin/campaigns/receipts?limit=${limit}`);
  }

  // What a launch will cost, priced call by call from the prompt it will actually send.
  // Server-side: the thing that assembles the prompt is the thing that prices it, so a
  // corpus swap that multiplies the input tokens cannot go unnoticed by the panel.
  async getReelCostEstimate(params) {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')),
    );
    return this.makeRequest(`/api/admin/reel-cost-estimate?${q}`);
  }

  // Jobs parked waiting for a human to pick between candidates.
  async getReelSelections() {
    return this.makeRequest("/api/admin/reel-selections");
  }

  async pickReelSelection(jobId, index) {
    return this.makeRequest(`/api/admin/reel-selections/${jobId}/pick`, {
      method: "POST",
      body: JSON.stringify({ index }),
    });
  }

  // `count` is how many candidates to come back with; null keeps the job's own number.
  // ONE submission for the whole reel: {sceneIndex: takeIndex}. The resume assembles from
  // those clips and regenerates nothing.
  async pickReelScenes(jobId, sceneTakes) {
    return this.makeRequest(`/api/admin/reel-selections/${jobId}/pick`, {
      method: "POST",
      body: JSON.stringify({ scene_takes: sceneTakes }),
    });
  }

  // Approve the narration you just listened to. `segments` is the full list as you are
  // approving it — unchanged entries keep the clip that was already spoken, edited ones are
  // re-spoken and only those are paid for again.
  async approveReelAudio(jobId, segments) {
    return this.makeRequest(`/api/admin/reel-selections/${jobId}/pick`, {
      method: "POST",
      body: JSON.stringify({ segments }),
    });
  }

  // Abandon a parked reel: drops its takes and cancels the job.
  async discardReelSelection(jobId) {
    return this.makeRequest(`/api/admin/reel-selections/${jobId}`, { method: "DELETE" });
  }

  /** WHAT A SEND-BACK WILL ADD TO THE NEXT PROMPT, before spending anything on it. Built
   *  server-side from the same function that assembles the writer's turn, so it cannot
   *  describe a prompt that differs from the one sent. */
  async redoReelPreview(jobId, instructions = "") {
    const q = instructions ? `?instructions=${encodeURIComponent(instructions)}` : "";
    return this.makeRequest(`/api/admin/reel-selections/${jobId}/redo-preview${q}`);
  }

  /** `favourite` is the index of the draft that was CLOSEST, or null for "none of them".
   *  It is carried apart from the rejected list: the rewrite is told to build on this one
   *  and to avoid the others, and putting a draft in both lists is a contradiction the
   *  model resolves by picking a side. */
  async redoReelSelection(jobId, instructions, count = null, favourite = null) {
    return this.makeRequest(`/api/admin/reel-selections/${jobId}/redo`, {
      method: "POST",
      body: JSON.stringify({ instructions, count, favourite }),
    });
  }

  // Per-scene cost medians from real finished reels; the estimate is computed client-side
  // so toggling a checkbox does not cost a round trip.
  async getReelCostBasis() {
    return this.makeRequest("/api/admin/reel-cost-basis");
  }

  // ─── Admin Reel Ideas ───
  /** The judge's rubric, plus the reviser's brief AS THIS PACKAGE RESOLVES IT.
   *  The rubric stays global — a score has to mean the same thing across packages — but a
   *  package may ship its own revise_brief.txt / revise_intro.txt, so the corpus is part of
   *  the question. Omit it for the shipped text. */
  async getPipelinePrompts(corpus = "") {
    const q = corpus ? `?corpus=${encodeURIComponent(corpus)}` : "";
    return this.makeRequest(`/api/admin/pipeline-prompts${q}`);
  }

  async getReelIdeas() {
    return this.makeRequest("/api/admin/reel-ideas");
  }

  async createReelIdea(text) {
    return this.makeRequest("/api/admin/reel-ideas", {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  }

  async deleteReelIdea(ideaId) {
    return this.makeRequest(`/api/admin/reel-ideas/${ideaId}`, {
      method: "DELETE",
    });
  }

  async updateReelIdea(ideaId, data) {
    return this.makeRequest(`/api/admin/reel-ideas/${ideaId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // ─── Admin: measured inputs for the capacity model ───
  async getCapacityInputs(days = 90) {
    return this.makeRequest(`/api/admin/capacity-inputs?days=${days}`);
  }

  // ─── Admin: provider rate headroom ───
  async getRateHeadroom(hours = 6) {
    return this.makeRequest(`/api/admin/rate-headroom?hours=${hours}`);
  }

  // ─── Admin Support (in-app conversations) ───
  async getSupportConversations(status, limit = 50) {
    const params = new URLSearchParams({ limit });
    if (status) params.set("status", status);
    return this.makeRequest(`/api/admin/support/conversations?${params}`);
  }

  async getSupportConversationAdmin(uid) {
    return this.makeRequest(`/api/admin/support/conversations/${uid}`);
  }

  async getSupportPendingCount() {
    return this.makeRequest("/api/admin/support/pending-count");
  }

  async replySupportConversation(uid, text) {
    return this.makeRequest(`/api/admin/support/conversations/${uid}/reply`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  }

  async markSupportConversationRead(uid) {
    return this.makeRequest(`/api/admin/support/conversations/${uid}/read`, {
      method: "POST",
    });
  }

  async closeSupportConversation(uid) {
    return this.makeRequest(`/api/admin/support/conversations/${uid}/close`, {
      method: "POST",
    });
  }

  // ─── User sync (registration / login / upgrade) ───
  // timezone/browserLocale are read here rather than at the call site so no future
  // caller can forget them: they are the only country signal that exists for
  // ANONYMOUS visitors, who have no Google profile at all. The backend consults
  // them only after the LB header and server-side GeoIP come back empty.
  async syncUser({ email, displayName, photoURL, isAnonymous, authMethod, emailMarketingOptIn, googleLocale, priorAnonUid }) {
    // Read here rather than threaded through every caller: it is ambient, the same for every
    // sync in the session, and the backend only stores it when creating the document.
    let acquisitionSource = null;
    try {
      acquisitionSource = (await import("./utils/acquisition")).getSource();
    } catch { /* attribution is best-effort and must never block a sign-in */ }
    let timezone = null;
    let browserLocale = null;
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch { /* ancient/locked-down browser — geo stays best-effort */ }
    try {
      browserLocale = navigator.language || null;
    } catch { /* ditto */ }
    return this.makeRequest("/api/auth/user/sync", {
      method: "POST",
      body: JSON.stringify({ email, displayName, photoURL, isAnonymous, authMethod, emailMarketingOptIn, googleLocale, timezone, browserLocale, priorAnonUid: priorAnonUid || null, acquisitionSource }),
    });
  }

  // Re-point work made while anonymous at the account that just signed in. Only needed when
  // the anonymous session could NOT be linked (the account already existed), because linking
  // keeps the same uid and nothing has to move.
  // Our own confirmation email, sent over Brevo rather than by Firebase. Resolves with
  // {sent:false} rather than throwing on a server-side refusal, so callers branch on `sent`
  // and fall back to Firebase's email — see auth.js sendVerification().
  async sendVerificationEmail() {
    return this.makeRequest("/api/auth/user/send-verification", { method: "POST" });
  }

  async claimAnonVideos(anonUid) {
    return this.makeRequest("/api/auth/user/claim-anon-videos", {
      method: "POST",
      body: JSON.stringify({ anon_uid: anonUid }),
    });
  }

  // Trial terms come from the server so the modal cannot disclose a price or duration that
  // differs from what Stripe will actually charge.
  async getTrialConfig() {
    return this.makeRequest("/api/payment/trial-config");
  }

  async updateEmailOptIn(optIn) {
    return this.makeRequest("/api/auth/user/email-opt-in", {
      method: "POST",
      body: JSON.stringify({ opt_in: optIn }),
    });
  }

  async getUserProfile(userId) {
    return this.makeRequest(`/api/auth/user/${userId}/profile`);
  }

  async updateUserProfile(userId, data) {
    return this.makeRequest(`/api/auth/user/${userId}/profile`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async trackAnalytics(userId, field, value, incrementBy) {
    // `${undefined}` interpolates to the STRING "undefined", which the backend rejects with a
    // 401 — and a 401 used to be read as a dead session. Callers pass `user.uid` from state
    // that is briefly an object without one, so this is cheaper to stop here than at ten call
    // sites. Analytics is fire-and-forget; a skipped datapoint costs nothing.
    if (!userId || userId === "undefined" || userId === "null") return;
    const body = { field };
    if (incrementBy !== undefined) body.increment_by = incrementBy;
    else body.value = value;
    return this.makeRequest(`/api/auth/user/${userId}/analytics`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  // ─── Support (user-facing in-app messaging; multi-thread) ───
  async listSupportConversations() {
    return this.makeRequest("/api/auth/user/support/conversations");
  }

  async createSupportConversation(text, clientId) {
    return this.makeRequest("/api/auth/user/support/conversations", {
      method: "POST",
      body: JSON.stringify({ text, ...(clientId ? { client_id: clientId } : {}) }),
    });
  }

  async sendSupportMessage(conversationId, text, clientId) {
    return this.makeRequest(`/api/auth/user/support/conversations/${conversationId}/message`, {
      method: "POST",
      body: JSON.stringify({ text, ...(clientId ? { client_id: clientId } : {}) }),
    });
  }

  async markSupportRead(conversationId) {
    return this.makeRequest(`/api/auth/user/support/conversations/${conversationId}/read`, {
      method: "POST",
    });
  }

  async deleteAccount(userId) {
    return this.makeRequest(`/api/auth/user/${userId}`, {
      method: "DELETE",
    });
  }

  // GDPR data export (Art. 15/20) — returns all of the user's data as JSON.
  // Heavier than a normal call (gathers every collection), so give it a longer timeout.
  async exportUserData(userId) {
    return this.makeRequest(`/api/auth/user/${userId}/export`, { timeoutMs: 120000 });
  }

  // Video chat - ask questions about video content
  async sendVideoChat(videoId, message, conversationHistory = []) {
    return this.makeRequest(`/api/video-chat/${videoId}`, {
      method: "POST",
      body: JSON.stringify({
        message: message,
        conversation_history: conversationHistory,
      }),
    });
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();
export default apiService;
